/**
 * «Asistente» rollout (plan §9.1, §9.14): since GA every store has the panel
 * (before it, staff and the pilot stores did). A per-device override
 * ('on' / 'off', set with `?assistant=`) beats the rule. The panel only
 * exists inside a store; pages without it keep the GPTHelp bubble.
 */
export const ASSISTANT_GA = true;

/**
 * Store slugs of the pilot. Irrelevant while `ASSISTANT_GA` is on; they get
 * the panel again if GA is switched off (plan §14.3).
 */
export const ASSISTANT_PILOT_STORES: readonly string[] = [
  'bobas',
  'bobasprueba',
];

export type TAssistantOverride = 'default' | 'on' | 'off';

export const ASSISTANT_OVERRIDES: readonly TAssistantOverride[] = [
  'default',
  'on',
  'off',
];

export function isAssistantOverride(
  value: unknown
): value is TAssistantOverride {
  return (
    typeof value === 'string' &&
    (ASSISTANT_OVERRIDES as readonly string[]).includes(value)
  );
}

export interface IAssistantRolloutInput {
  override: TAssistantOverride;
  isStaff: boolean;
  /** Slug of the current store; null outside a store. */
  stringId: string | null;
}

export function isAssistantEnabled(input: IAssistantRolloutInput): boolean {
  if (!input.stringId) return false;
  if (input.override === 'on') return true;
  if (input.override === 'off') return false;
  return (
    ASSISTANT_GA ||
    input.isStaff ||
    ASSISTANT_PILOT_STORES.includes(input.stringId.toLowerCase())
  );
}

export interface IHelpBubbleInput {
  /** The first navigation resolved (the route guard has loaded the store). */
  routeReady: boolean;
  /** The browser can run the GPTHelp widget (`isGpthelpSupportedBrowser`). */
  supported: boolean;
  /** The panel exists: a logged-in user in a store the rollout enables. */
  panelEnabled: boolean;
  /** A POS workspace (route meta `pos`). */
  posRoute: boolean;
}

/**
 * The GPTHelp bubble shows wherever the panel does not exist (plan §9.14,
 * owner decision 2026-09-30): logged-out pages, pages outside a store and
 * stores the rollout leaves out (at GA, only with `?assistant=off`); never on
 * POS workspaces, nor on browsers that cannot run the widget. It is never
 * shown together with the panel, so two `<gpt-help>` elements never coexist.
 */
export function isHelpBubbleShown(input: IHelpBubbleInput): boolean {
  return (
    input.routeReady &&
    input.supported &&
    !input.panelEnabled &&
    !input.posRoute
  );
}
